import * as z from "zod/mini";
import { CONNECTION_FORCE_ERROR_SCENARIOS } from "@connect/data/mocks/force-error-scenarios";
import type { ConfigValidationFieldError } from "@moonpay/platform-protocol/models/connection";
import { devBaseSchema, parseFrameConfig, themeBaseSchema } from "../../lib/parse-frame-config";

// --- Public params (documented in Mintlify) ---
const publicSchema = z.extend(themeBaseSchema, {
  channelId: z._default(z.string(), ""),
  sessionToken: z._default(z.string(), ""),
  publicKey: z.optional(z.string()),
  // Opts out of ramps-derived blocks (kyc_required, pending, unavailable);
  // terms_acceptance_required always still surfaces. Mirrors Orion's
  // checkConnection skipKyc param.
  skipKyc: z.catch(z._default(z.stringbool(), false), false),
});

// --- Dev-only params (ground-control internal) ---
const devSchema = z.object({
  ...devBaseSchema.shape,
  hasConnection: z.pipe(
    z._default(z.string(), "true"),
    z.transform((v) => v !== "false"),
  ),
  forceError: z._default(z.enum(["", ...CONNECTION_FORCE_ERROR_SCENARIOS.map((s) => s.value)]), ""),
});

const KNOWN_KEYS = new Set([...Object.keys(publicSchema.shape), ...Object.keys(devSchema.shape)]);

export type FrameConfig = z.infer<typeof publicSchema> & z.infer<typeof devSchema>;

export function parseConfig(search?: string): FrameConfig {
  const { config, devConfig } = parseFrameConfig({
    publicSchema,
    devSchema,
    knownKeys: KNOWN_KEYS,
    search,
  });
  return { ...config, ...devConfig };
}

const X25519_HEX = /^(0x)?[0-9a-fA-F]{64}$/;

export function validateConfig(config: FrameConfig): ConfigValidationFieldError[] {
  const errors: ConfigValidationFieldError[] = [];
  if (!config.sessionToken) {
    errors.push({
      code: "invalidSessionToken",
      message: "sessionToken is required",
    });
  }
  if (!config.publicKey) {
    errors.push({
      code: "invalidPublicKey",
      message: "publicKey is required",
    });
  } else if (!X25519_HEX.test(config.publicKey)) {
    errors.push({
      code: "invalidPublicKey",
      message: "publicKey must be 64 hex characters (32 bytes) representing an X25519 public key",
    });
  }
  return errors;
}
